import type { Membership } from "./auth";

/**
 * Which organisation the dashboard is acting as, on load.
 *
 * Nothing the client sends scopes a request to an org — the API gateway derives
 * it from the token's `org_id` claim and, when that claim names no active
 * membership, resolves the user's default instead
 * (services/api/src/lib/memberships-cache.ts `resolveOrgFromMemberships`). The
 * client therefore cannot pick an org freely: whatever it displays must be the
 * org the gateway will serve, or the sidebar names one account while every
 * request returns another's data — an empty list reads as "everything was
 * deleted" and any id from the named org 404s.
 *
 * So the client does not re-derive that decision, it reads it. `GET
 * /users/me/memberships` returns `activeOrgId`: the value the gateway itself
 * resolved, from the gateway's own function on the gateway's own rows. The
 * `data` array stays a display ordering and is not a resolution input.
 *
 * Mirroring the gateway's rule here was tried first and cannot work. It orders
 * on `created_at` at microsecond precision, which node-pg truncates to
 * milliseconds before any client sees it, then breaks the remaining tie on
 * `org_id` under the server's collation, which no JavaScript comparator
 * reproduces. Both are unrecoverable browser-side, so a mirror is a third
 * implementation of a decision that already had two, and it drifts by
 * construction.
 */

export interface ActiveOrgInput {
  /**
   * `activeOrgId` from `GET /users/me/memberships` — the gateway's decision.
   *
   * `null` is an answer, not a missing value: the api declined to resolve and
   * wants the caller left on the token's context. `undefined` means the field
   * was absent, i.e. an api that predates it, which is the only case the
   * deploy-window shim below is for.
   */
  activeOrgId?: string | null;
  /** `org_id` from the JWT. Only read by the deploy-window fallback below. */
  claimedOrgId?: string;
  /** Active memberships, as returned by `GET /users/me/memberships`. */
  memberships: Membership[];
}

/**
 * Resolve the membership the dashboard should act as. Returns null when the
 * user has no active memberships — mid-signup, where the caller must leave the
 * token's context alone so auto-provisioning can run.
 *
 * `activeOrgId` is looked up in the list rather than trusted blindly: both come
 * from the same response, so they can only disagree when the resolved org is
 * absent from `data`, and pinning the UI to a row it cannot render is worse
 * than falling through.
 */
export function resolveActiveOrg({ activeOrgId, claimedOrgId, memberships }: ActiveOrgInput): Membership | null {
  if (memberships.length === 0) return null;

  // Declined, not absent — honour it and leave the caller's context alone.
  if (activeOrgId === null) return null;

  const served = activeOrgId ? memberships.find((m) => m.orgId === activeOrgId) : undefined;
  if (served) return served;

  // Deploy-window shim, not a resolver. The dashboard is a static bundle and
  // ships independently of the api pods, so a new bundle can talk to an api
  // that predates `activeOrgId` for the length of a rollout. This is the exact
  // rule the client used before that field existed, kept only so that window is
  // no worse than the release before it. It is knowingly not the gateway's
  // rule — that is the entire reason `activeOrgId` exists. Delete it once no
  // deployed api can answer without the field.
  const claimed = claimedOrgId ? memberships.find((m) => m.orgId === claimedOrgId) : undefined;
  return claimed ?? memberships.find((m) => m.isDefault) ?? memberships[0];
}
